/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DoCheck, Inject, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import {AdminResourceItemPageBase} from '../common/controller/admin-resource-item-page-base';
import {AdServerConfig} from '../../components/api/api';
import {AdTagReplacers} from './ad-tag-replacers';
import {calculateNetCpm} from '../line-item/fees-calculator';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {AdUnitCreativeTotalsPreviewModalComponent} from './ad-unit-creative-totals-preview-modal.component';
import {SearchResource} from '../../components/api/resources/resource/search';
import {DiagRequestResource} from '../../components/api/resources/resource/diag-request';
import * as _ from 'lodash';
import template from './ad-unit-analysis-page.component.html';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {AdUnitTagResource} from '../../components/api/resources/resource/adunittag.resource';
import {EarningsAccountResource} from '../../components/api/resources/resource/earnings-account.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {RulesetResource} from '../../components/api/resources/resource/ruleset.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

type AnalysisTab = 'adUnit' | 'external' | 'tag' | 'reports' | 'traffic' | 'location' | 'availability' | 'diagnostics' | 'explore';

interface DisplayItem {
  bold?: string;
  text?: string;
  label?: string;
  subItems?: string[];
}

@Component({
  standalone: false,
  selector: 'adn-ad-unit-analysis-page',
  template
})
export class AdUnitAnalysisPageComponent extends AdminResourceItemPageBase implements DoCheck, OnChanges, OnDestroy {
  apiModel: any;
  activeTab: AnalysisTab = 'adUnit';

  networkTagId: string;
  chartCurrency: any;
  canSeeReports = false;
  isPureMarketplace = false;

  idType: 'CUSTOM' | 'MAILCHIMP' = 'CUSTOM';
  defaultMailchimp = '*|CAMPAIGN_UID|**|UNIQID|*';
  uniqueId = '';
  emailCode = '';
  vast2 = '';
  vast3 = '';
  vast4 = '';
  vast4_1 = '';
  vast4_2 = '';
  native = '';
  whitelabel = false;
  whitelabelDomain = '';
  selTag: {name: string; order?: number} = {name: 'COM'};

  showingChart: string | null = null;
  showingTraffic = false;
  showingLocation = false;
  showingReach = false;
  showingExplorer = false;
  visibleReports = false;

  showTargeting = false;
  diag: any;
  diagRunning = false;
  diagResults = false;
  diagLineItemId: string | null = null;
  error: string | null = null;
  errorInfo: string | null = null;
  floorPrice: any = null;
  country: string;
  city: string;
  temperature: any;
  temperatureLocation: string;
  entryCost: string;
  politicalApprovalRequired = false;
  showLocationInfo = false;

  winning: any[] = [];
  winningResults = false;
  competing: any[] = [];
  competingResults = false;
  selected: any[] = [];
  selectedResults = false;
  eligible: any[] = [];
  eligibleResults = false;
  ineligible: any[] = [];
  ineligibleResults = false;
  unsuitable: any[] = [];
  unsuitableResults = false;
  totalLineItems = 0;

  private totalsData: any[];
  private creativeSorting: any;
  private lastLoadedTagsModelId: string | undefined;
  private lastSeenEffectiveIsNew: boolean | undefined;
  private tagsSubscribedModelComms: any;
  private destroyed = false;

  private readonly ogEmail = '<a href="https://delivery.adnuntius.com/c?network=[[TAG_ID]]&auId=[[AD_UNIT]]&autor=true&iid=[THIS_MUST_BE_REPLACED]">\n    <img src="https://delivery.adnuntius.com/i?network=[[TAG_ID]]&auId=[[AD_UNIT]]&format=image&iid=[THIS_MUST_BE_REPLACED]"[[AD_UNIT_WIDTH]][[AD_UNIT_HEIGHT]] />\n</a>\n';
  private readonly ogVast: string;
  private readonly ogNative: string;

  readonly callbackHook = (): void => {
    this.doPreviews();
  };

  readonly statsHook = (totals: any, sorting: any): void => {
    if (totals) {
      this.totalsData = _.cloneDeep(totals);
    }
    this.creativeSorting = _.cloneDeep(sorting);
  };

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdUnitTagResource) private readonly AdUnitTag: any,
    private readonly diagRequestResource: DiagRequestResource,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(SiteResource) private readonly Site: any,
    @Inject(EarningsAccountResource) private readonly EarningsAccount: any,
    @Inject(RulesetResource) private readonly Ruleset: any,
    private readonly searchResource: SearchResource,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone,
    cdr: ChangeDetectorRef,
    private readonly appLocale: AppLocaleService
  ) {
    super(cdr);
    this.networkTagId = LocalNetworkProfile.get('tagId');
    this.ogVast = AdServerConfig.obtain().getUri('i') + '?network=[[TAG_ID]]&auId=[[AD_UNIT]]&tt=[[VAST_VERSION]]';
    this.ogNative = AdServerConfig.obtain().getUri('i') + '?network=[[TAG_ID]]&auId=[[AD_UNIT]]&tt=native';
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  get hasExternalAdUnits(): boolean {
    return (this.model?.externalAdUnits?.length || 0) > 0;
  }

  get tagNavItems(): any[] {
    return _.filter(this.model?.tags?.tags || [], (tag) => tag.name !== 'FIF');
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.isNew) {
      this.lastSeenEffectiveIsNew = this.effectiveIsNew;
      if (this.effectiveIsNew) {
        this.lastLoadedTagsModelId = undefined;
      }
    }
    if (changes.model) {
      this.apiModel = this.model;
      this.chartCurrency = _.get(this.model, ['floorPrice', 'currency']);
      this.initDiag();
      this.loadTagsIfNeeded();
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngDoCheck(): void {
    if (this.lastSeenEffectiveIsNew !== this.effectiveIsNew) {
      if (this.lastSeenEffectiveIsNew === true && this.effectiveIsNew === false) {
        this.lastLoadedTagsModelId = undefined;
      }
      this.lastSeenEffectiveIsNew = this.effectiveIsNew;
    }
    this.loadTagsIfNeeded();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'external') {
      this.showingChart = 'EXTERNAL_AD_UNIT';
    } else if (tab === 'reports') {
      this.visibleReports = true;
    } else if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    } else if (tab === 'availability') {
      this.showingReach = true;
    } else if (tab === 'explore') {
      this.showingExplorer = true;
    }
  }

  changeValue(): void {
    this.updateTagCode();
  }

  selectTagName(name: string, event?: Event): void {
    event?.preventDefault();
    this.selTag.name = name;
  }

  addKv(): void {
    this.diag.kv.push({k: '', v: ''});
  }

  removeKv(index: number): void {
    _.pullAt(this.diag.kv, index);
  }

  searchLineItems(term: string): Promise<any[]> {
    if (!term) {
      return Promise.resolve([]);
    }
    return this.searchResource.query({q: term, types: 'LineItem', pageSize: 10}).then((data: any) => {
      return data.searchResults || [];
    });
  }

  doPreviews(): void {
    this.angularModalService.show(AdUnitCreativeTotalsPreviewModalComponent, {
      class: 'modal-lg',
      initialState: {
        totalsData: this.totalsData,
        creativeSorting: this.creativeSorting
      }
    });
  }

  runDiag(): void {
    this.diagRunning = true;
    this.diagResults = false;
    this.error = null;
    this.errorInfo = null;
    this.diagLineItemId = _.trim(this.diag.dxLineItemId) || null;
    this.resolveFloorPrice();

    this.diagRequestResource.dx(this.diag).then((data: any) => {
      this.ngZone.run(() => {
        const lineItemParticipants = _.isArray(data.lineItemParticipants) ? data.lineItemParticipants : [];
        _.forEach(lineItemParticipants, (l) => {
          this.processLineItemParticipant(l);
        });

        this.diagRunning = false;
        this.diagResults = true;
        this.country = data.country;
        this.city = data.city;
        this.temperature = data.temperature;
        this.temperatureLocation = data.temperatureLocation;
        this.entryCost = this.normaliseEntryCost(data.entryCost);
        this.politicalApprovalRequired = data.politicalApprovalRequired === true;

        this.doQueries('winning', 30, lineItemParticipants);
        this.doQueries('selected', 30, lineItemParticipants);
        this.doQueries('selected', 30, lineItemParticipants, true);
        this.doQueries('eligible', 30, lineItemParticipants);
        this.doQueries('unsuitable', 30, lineItemParticipants);
        this.doQueries('ineligible', 10, lineItemParticipants);

        const unsuitable = this.unsuitable || [];
        const ineligible = this.ineligible || [];
        this.ineligible = ineligible.concat(unsuitable);
        this.totalLineItems = lineItemParticipants.length;
        safeDetectChanges(this.cdr);
      });
    }).catch((response: any) => {
      this.ngZone.run(() => {
        this.errorInfo = response.info;
        this.error = response.clientError || response.statusText || '';
        this.diagRunning = false;
        safeDetectChanges(this.cdr);
      });
    });
  }

  private loadTagsIfNeeded(): void {
    if (this.effectiveIsNew) {
      return;
    }
    const modelId = this.model?.id;
    if (!modelId) {
      return;
    }
    if (this.lastLoadedTagsModelId === modelId) {
      return;
    }
    this.lastLoadedTagsModelId = modelId;

    if (this.model.tags?.auId) {
      this.initTags();
      this.updateTagCode();
      return;
    }
    this.AdUnitTag.get({id: modelId}).$promise.then((tags: any) => {
      this.ngZone.run(() => {
        this.model.tags = tags;
        this.initTags();
        this.updateTagCode();
        safeDetectChanges(this.cdr);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        if (this.lastLoadedTagsModelId === modelId) {
          this.lastLoadedTagsModelId = undefined;
        }
      });
    });
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.tagsSubscribedModelComms === this.modelComms) {
      return;
    }
    this.tagsSubscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any) => {
      if (this.destroyed || type || !_.get(data, 'name')) {
        return;
      }
      this.ngZone.run(() => {
        Object.assign(this.model, data);
        this.whitelabelDomain = AdTagReplacers.getNetworkWhitelabelDomain(this.LocalNetworkProfile.getNetworkTag());
        this.whitelabel = !!this.whitelabelDomain;
        if (!this.effectiveIsNew) {
          this.lastLoadedTagsModelId = undefined;
          this.loadTagsIfNeeded();
        } else {
          this.updateTagCode();
        }
        safeDetectChanges(this.cdr);
      });
    });
  }

  private initDiag(): void {
    const auId = this.model?.tags?.auId;
    this.diag = {
      auId: auId,
      tzo: new Date().getTimezoneOffset(),
      kv: [{k: '', v: ''}],
      requestType: 'NORMAL'
    };
  }

  private initTags(): void {
    this.updateSelTags();
    if (_.get(this.model, ['tags', 'tags'])) {
      const tagNames = _.map(this.model.tags.tags, 'name');
      if (!tagNames.includes('email')) {
        this.model.tags.tags.push({name: 'email'});
      }
      if (!tagNames.includes('vast')) {
        this.model.tags.tags.push({name: 'vast'});
      }
      if (!tagNames.includes('native')) {
        this.model.tags.tags.push({name: 'native'});
      }
    }
    this.whitelabelDomain = AdTagReplacers.getNetworkWhitelabelDomain(this.LocalNetworkProfile.getNetworkTag());
    this.whitelabel = !!this.whitelabelDomain;
    this.initDiag();
    this.updateTagCode();
  }

  private updateSelTags(): void {
    this.selTag = {name: 'COM'};
    _.forEach(this.model?.tags?.tags, (tag) => {
      if (tag.name === 'COM') {
        tag.order = 1;
        return;
      }
      tag.order = 2;
    });
  }

  private updateTagCode(): void {
    this.emailCode = '';
    const auTagId = _.get(this.model, ['tags', 'auId']);
    if (!auTagId) {
      return;
    }

    let adUnitString = this.ogEmail.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId);
    const widthReplacer = this.model.width ? (' width="' + this.model.width + '"') : '';
    const heightReplacer = this.model.height ? (' height="' + this.model.height + '"') : '';
    adUnitString = adUnitString.replace('[[AD_UNIT_WIDTH]]', widthReplacer).replace('[[AD_UNIT_HEIGHT]]', heightReplacer);

    if (this.idType === 'MAILCHIMP') {
      adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.defaultMailchimp);
    } else if (this.uniqueId) {
      adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.uniqueId);
    }
    this.emailCode = this.emailCode + adUnitString;

    this.vast2 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast2');
    this.vast3 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast3');
    this.vast4 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4');
    this.vast4_1 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4-1');
    this.vast4_2 = this.ogVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId).replace(/\[\[VAST_VERSION]]/g, 'vast4-2');
    this.native = this.ogNative.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, auTagId);

    if (this.whitelabel && this.whitelabelDomain) {
      this.emailCode = AdTagReplacers.replace(this.emailCode, this.whitelabelDomain, this.networkTagId);
      this.vast2 = AdTagReplacers.replace(this.vast2, this.whitelabelDomain, this.networkTagId);
      this.vast3 = AdTagReplacers.replace(this.vast3, this.whitelabelDomain, this.networkTagId);
      this.vast4 = AdTagReplacers.replace(this.vast4, this.whitelabelDomain, this.networkTagId);
      this.vast4_1 = AdTagReplacers.replace(this.vast4_1, this.whitelabelDomain, this.networkTagId);
      this.vast4_2 = AdTagReplacers.replace(this.vast4_2, this.whitelabelDomain, this.networkTagId);
      this.native = AdTagReplacers.replace(this.native, this.whitelabelDomain, this.networkTagId);
    }
  }

  private resolveFloorPrice(): void {
    this.floorPrice = null;
    if (this.model?.floorPrice?.amount) {
      this.floorPrice = this.model.floorPrice;
    } else if (this.model?.siteRuleset?.id) {
      this.Ruleset.get(this.model.siteRuleset, (siteRuleset: any) => {
        this.ngZone.run(() => {
          this.floorPrice = siteRuleset.floorPrice;
          safeDetectChanges(this.cdr);
        });
      });
    } else if (this.model?.site?.id) {
      this.Site.get(this.model.site, (site: any) => {
        if (site.siteRuleset?.id) {
          this.Ruleset.get(site.siteRuleset, (siteRuleset: any) => {
            this.ngZone.run(() => {
              this.floorPrice = siteRuleset.floorPrice;
              safeDetectChanges(this.cdr);
            });
          });
        } else if (site.earningsAccount?.id) {
          this.EarningsAccount.get(site.earningsAccount, (earningsAccount: any) => {
            if (earningsAccount.siteRuleset?.id) {
              this.Ruleset.get(earningsAccount.siteRuleset, (siteRuleset: any) => {
                this.ngZone.run(() => {
                  this.floorPrice = siteRuleset.floorPrice;
                  safeDetectChanges(this.cdr);
                });
              });
            }
          });
        }
      });
    }
  }

  private processLineItemParticipant(l: any): void {
    const joinKeys = ['unmatchedAds', 'matchedAds', 'winningAds', 'competingExternalAds'];
    _.forEach(joinKeys, (k) => {
      const arr = _.isArray(l[k]) ? l[k] : [];
      l[k + 'Count'] = arr.length;
      l[k] = arr.join(', ');
    });
    l.lossReasons = _.isArray(l.lossReasons) ? l.lossReasons : [];
    l.lossReasonsCount = l.lossReasons.length;

    const matched = _.map(l.matchedExpressions || [], (t: string) => this.buildExpressionItem(t, false));
    const negatedMatched = _.map(l.negatedMatchedExpressions || [], (t: string) => this.buildExpressionItem(t, true));
    l.matchedDisplay = matched.concat(negatedMatched);

    const unmatchedTypes = this.buildUnmatchedTypeItems(l.unmatchedExpressionTypes);
    const negatedUnmatched = _.map(l.negatedUnmatchedExpressions || [], (t: string) => this.buildExpressionItem(t, true));
    const unmatchedFilters = this.buildUnmatchedFilterItems(l.unmatchedFilters);
    l.unmatchedDisplay = unmatchedTypes.concat(negatedUnmatched).concat(unmatchedFilters);

    l.blocklistDiagnostics = _.isArray(l.blocklistDiagnostics) ? l.blocklistDiagnostics : [];
    l.blocklistDiagnosticsCount = l.blocklistDiagnostics.length;
    l.floorDiagnosticDetails = this.normaliseFloorDiagnostics(l.floorDiagnostics);
    l.floorDiagnostics = _.map(l.floorDiagnosticDetails, 'summary');
    l.floorDiagnosticsCount = l.floorDiagnostics.length;
    l.hasMoreDiagnosticInfo = l.floorDiagnosticsCount > 0 || l.blocklistDiagnosticsCount > 0;
  }

  private doQueries(key: string, max: number, lineItemParticipants: any[], external?: boolean): void {
    const newKey = external ? 'competing' : key;
    this[newKey + 'Results'] = false;
    this[newKey] = _.filter(lineItemParticipants, (l) => {
      const matchedStage = l.reachedStage === key.toUpperCase();
      if (matchedStage && key.toUpperCase() !== 'SELECTED') {
        return matchedStage;
      }
      if (external) {
        return matchedStage && l.competingExternalAdsCount > 0;
      }
      return matchedStage && l.competingExternalAdsCount < 1;
    });
    const ids = _.map(this[newKey], 'externalId');
    if (ids.length === 0) {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this[newKey + 'Results'] = true;
      });
      return;
    }
    this.LineItem.query({id: ids.slice(0, max).join(';')}).$promise.then((data: any) => {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        const lineItems = data.results;
        _.forEach(this[newKey], (l) => {
          const li = _.find(lineItems, ['id', l.externalId]);
          if (li) {
            l.lineItemName = _.get(li, 'name');
            l.type = li.type;
            if (li.type === 'SPONSORSHIP') {
              l.sponsorshipPercentage = li.sponsorshipPercentage;
            } else {
              l.smoothed = li.smoothed;
            }
            l.tier = li.tier;
            l.bidSpecification = li.bidSpecification;
            l.objectives = li.objectives;
            if (l.bidSpecification?.cpm) {
              const fees = (l.bidSpecification.cpmFees || []);
              if (l.bidSpecification.cpmTechFee) {
                fees.push(l.bidSpecification.cpmTechFee);
              }
              l.bidSpecification.cpm = calculateNetCpm(fees, l.bidSpecification.cpm);
            }
          }
        });
        this[newKey + 'Results'] = true;
      });
    });
  }

  private cleanDiagnosticText(value: any): string {
    return _.isNil(value) ? '' : _.trim(String(value));
  }

  private formatMoneyAsCpm(value: any): string {
    const cleanedValue = this.cleanDiagnosticText(value);
    if (!cleanedValue) {
      return 'Unknown';
    }
    if (/CPM$/i.test(cleanedValue)) {
      return cleanedValue;
    }
    const moneyMatch = cleanedValue.match(/^([A-Z]{3})\s+(-?\d+(?:\.\d+)?)$/);
    if (moneyMatch) {
      const cpmValue = Number(moneyMatch[2]) * 1000;
      if (!Number.isNaN(cpmValue)) {
        return moneyMatch[1] + ' ' + cpmValue.toLocaleString(this.appLocale.getLocaleId(), {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }) + ' CPM';
      }
    }
    return cleanedValue;
  }

  private normaliseFloorSourceLabel(value: any): string {
    const cleanedValue = this.cleanDiagnosticText(value);
    if (!cleanedValue) {
      return 'floor';
    }
    return cleanedValue
      .replace(/^resolved\s+/i, '')
      .replace(/^ad unit floor$/i, 'ad unit floor')
      .replace(/^inventory ruleset floor(?:\s*\(rulesetId=.*\))?$/i, 'inventory floor')
      .replace(/^resolved inventory floor$/i, 'inventory floor');
  }

  private normaliseFloorDiagnostics(value: any): Array<{summary: string; details: string[]}> {
    const entries = _.isArray(value) ? value : [];
    return _.map(entries, (entry) => {
      let parsedEntry = entry;
      if (_.isString(entry)) {
        try {
          parsedEntry = entry.charAt(0) === '{' ? JSON.parse(entry) : null;
        } catch (e) {
          parsedEntry = null;
        }
      }

      if (!parsedEntry || !_.isPlainObject(parsedEntry)) {
        return {summary: this.cleanDiagnosticText(entry), details: []};
      }

      const creative = this.cleanDiagnosticText(parsedEntry.creative || 'unknown creative');
      const status = (parsedEntry.status || 'unknown').toLowerCase();
      const bid = this.formatMoneyAsCpm(parsedEntry.bid || 'Unknown');
      const resolvedFloor = this.formatMoneyAsCpm(parsedEntry.resolvedFloor || 'Unknown');
      const failedComponents = _.isArray(parsedEntry.failedComponents) ? parsedEntry.failedComponents : [];
      const applied = _.isArray(parsedEntry.applied) ? parsedEntry.applied.map((component: any) => this.cleanDiagnosticText(component)) : [];

      let summary: string;
      if (status === 'blocked' && failedComponents.length > 0) {
        const firstFailedComponent = failedComponents[0];
        const source = this.normaliseFloorSourceLabel(firstFailedComponent.source || firstFailedComponent.type || 'floor');
        const floor = this.formatMoneyAsCpm(firstFailedComponent.floor || resolvedFloor);
        summary = 'Creative ' + creative + ' blocked by ' + source + ': bid ' + bid + ', floor ' + floor + '.';
      } else if (status === 'blocked') {
        summary = 'Creative ' + creative + ' blocked by floor: bid ' + bid + ', floor ' + resolvedFloor + '.';
      } else if (status === 'bypassed') {
        summary = 'Creative ' + creative + ' bypassed floor checks: bid ' + bid + ', floor ' + resolvedFloor + '.';
      } else {
        summary = 'Creative ' + creative + ' passed floor checks: bid ' + bid + ', floor ' + resolvedFloor + '.';
      }

      const details: string[] = [];
      if (failedComponents.length > 1) {
        details.push('Failed components:');
      }
      _.forEach(failedComponents, (component) => {
        if (component && component.type) {
          const type = this.cleanDiagnosticText(component.type);
          const floor = this.formatMoneyAsCpm(component.floor || 'Unknown');
          const source = this.normaliseFloorSourceLabel(component.source || '');
          details.push(_.startCase(type) + ': ' + floor + (source ? ' (' + source + ')' : ''));
        }
      });
      if (applied.length > 0) {
        details.push('Applied: ' + applied.join(', '));
      }

      return {summary: summary, details: details};
    });
  }

  private normaliseEntryCost(value: any): string {
    if (_.isNil(value) || value === '') {
      return 'Unknown';
    }
    if (_.isNumber(value)) {
      return Number(value * 1000).toLocaleString(this.appLocale.getLocaleId(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }) + ' CPM';
    }
    return this.formatMoneyAsCpm(value);
  }

  private buildExpressionItem(text: string, negated: boolean): DisplayItem {
    const cleaned = this.cleanDiagnosticText(text);
    if (!negated) {
      return {text: cleaned};
    }
    return {bold: 'NOT', text: cleaned.replace(/^NOT\s+/, '')};
  }

  private buildUnmatchedTypeItems(value: any): DisplayItem[] {
    if (!_.isPlainObject(value)) {
      return [];
    }
    return _.map(value, (count, key) => {
      return {text: key + ' (x' + count + ')'};
    });
  }

  private buildUnmatchedFilterItems(value: any): DisplayItem[] {
    if (!_.isPlainObject(value)) {
      return [];
    }
    return _.map(value, (values, key) => {
      return {label: key + ':', subItems: _.isArray(values) ? values : []};
    });
  }
}
